import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import Tooltip from "@mui/material/Tooltip";
import { get_t_str_shortened_max, get_t_str_minute_precision, generateLaborSummary, formatName } from "../../../../helpers/helpers";
import { formatDateReadableWithDayOfWeek } from "../../requests/utilities/helpers";
import VisualCustomLineChart from "./VisualCustomLineChart";
import SurveyDetailModal from "../../guest_reviews/components/SurveyDetailModal";
import "./VisualDailyReportModal2.css";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 600,
  minHeight: 400,
  width: "99%",
  height: "99%",
  overflow: "auto",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
};

function GuestReviewOverlayModal(props) {
  const componentRef = React.useRef();

  // Use props instead of fetching
  const actualShifts = props.actual_used_schedule || [];
  const surveys = props.surveys?.surveys || props.surveys || [];

  // State for detailed review modal
  const [openReviewModal, setOpenReviewModal] = React.useState(false);
  const [selectedReview, setSelectedReview] = React.useState(null);

  const handleOpenReviewModal = (survey) => {
    setSelectedReview(survey);
    setOpenReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setOpenReviewModal(false);
    setSelectedReview(null);
  };

  // Debug logging
  React.useEffect(() => {
    if (props.open) {
      if (surveys.length > 0) {
        console.log('Surveys data:', surveys);
        surveys.forEach((s, i) => {
          console.log(`Survey ${i}: rating=${s.rating}, date=${s.date}, decimal_hour=${s.decimal_hour}, timestamp=${s.timestamp}, source=${s.source}`);
        });
      }
      if (actualShifts.length > 0) {
        console.log('Sample shift data:', actualShifts[0]);
        console.log('Sample shift keys:', Object.keys(actualShifts[0]));
      }
    }
  }, [props.open, surveys, actualShifts]);

  // Get color based on rating (matching Team Performance tab)
  const getRatingColor = (rating) => {
    if (rating === 5) return "bg-green-500";
    if (rating === 4) return "bg-lime-500";
    if (rating === 3) return "bg-yellow-500";
    if (rating === 2) return "bg-orange-500";
    return "bg-red-500"; // 1
  };

  // Group shifts by date (filter out invalid shifts)
  const groupShiftsByDate = () => {
    const grouped = {};

    actualShifts.forEach(shift => {
      // Skip invalid shifts where start and end are both 0 or null
      if ((shift.shift_start === 0 && shift.shift_end === 0) ||
          (shift.shift_start == null || shift.shift_end == null)) {
        return;
      }

      const date = shift.date_start?.split('T')[0] || shift.date_start;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(shift);
    });

    return grouped;
  };

  // Get surveys for a specific date
  const getSurveysForDate = (date) => {
    return surveys.filter(survey => (survey.business_date || survey.date) === date);
  };

  const getSurveyTimelineHour = (survey) => {
    return survey.business_decimal_hour ?? survey.decimal_hour;
  };

  const getSurveyDisplayTime = (survey) => {
    return get_t_str_minute_precision(survey.decimal_hour);
  };

  // Calculate timeline bounds
  const getTimelineBounds = () => {
    let minHour = props.start_qhnum || 0;
    let maxHour = 24;

    if (actualShifts.length > 0) {
      // Filter out invalid shifts (both start and end are 0) before calculating bounds
      const validShifts = actualShifts.filter(s =>
        !(s.shift_start === 0 && s.shift_end === 0) &&
        s.shift_start !== null && s.shift_start !== undefined &&
        s.shift_end !== null && s.shift_end !== undefined
      );

      if (validShifts.length > 0) {
        const starts = validShifts.map(s => s.shift_start);
        const ends = validShifts.map(s => s.shift_end);

        minHour = Math.floor(Math.min(...starts));
        maxHour = Math.ceil(Math.max(...ends));
      }
    }

    return { minHour, maxHour };
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={`full-${i}`} className="text-yellow-500" style={{ fontSize: '24px' }} />);
    }

    if (hasHalfStar) {
      stars.push(<StarHalfIcon key="half" className="text-yellow-500" style={{ fontSize: '24px' }} />);
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<StarOutlineIcon key={`empty-${i}`} className="text-yellow-500" style={{ fontSize: '24px' }} />);
    }

    return stars;
  };

  const shiftsByDate = groupShiftsByDate();
  const sortedDates = Object.keys(shiftsByDate).sort();
  const { minHour, maxHour } = getTimelineBounds();
  const globalStartQhnum = minHour;
  const numOfHourBoxes = maxHour - minHour;
  const widthOfEachHourBox = 100 / numOfHourBoxes;
  const widthOfEachQuarterTick = widthOfEachHourBox / 4;

  const hasShifts = actualShifts.length > 0;

  // Generate labor chart data
  const [laborChartData, setLaborChartData] = React.useState({ summary: [], roleTypes: [] });
  const label1 = "Earned";
  const label2 = "Used";

  React.useEffect(() => {
    if (props.calculated_earned_labor_day && props.actual_used_labor_vs_earned_day) {
      const newData = generateLaborSummary(
        props.calculated_earned_labor_day,
        props.actual_used_labor_vs_earned_day,
        props.get_t_str,
        label1,
        label2
      );
      setLaborChartData(newData);
    }
  }, [props.calculated_earned_labor_day, props.actual_used_labor_vs_earned_day]);

  // Get role name from role_id
  const getRoleName = (shift) => {
    // Try different possible paths for role data
    const roleObj = shift.role_id || shift.published?.role_id || shift.role;
    if (roleObj && typeof roleObj === 'object') {
      return roleObj.name || roleObj.role_name || roleObj.role || 'N/A';
    }
    if (typeof roleObj === 'string') {
      return roleObj;
    }
    return 'N/A';
  };

  return (
    <>
      <Modal
        open={props.open}
        onClose={props.handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
        <div
          className="absolute right-4 top-4 text-slate-500 cursor-pointer z-10"
          onClick={props.handleClose}
        >
          <CancelIcon />
        </div>

        <div className="p-6" ref={componentRef}>
          <div className="mb-4">
            <h2 className="font-bold text-xl">Guest Surveys Labor Overlay</h2>
            <div className="flex items-center mt-2">
              <p className="text-gray-600 mr-2 text-sm">Powered by</p>
              <img
                src="https://rightworkpublic.s3.us-west-1.amazonaws.com/ovation-logo.png"
                alt="Ovation"
                className="h-5"
              />
            </div>
          </div>

          <div className="mb-4 text-sm text-slate-600">
            <p>This view shows actual worked shifts with guest surveys overlaid at the time they occurred.</p>
            <div className="flex flex-wrap gap-6 mt-2">
              <div className="flex items-center gap-1">
                <div className="w-4 h-3 bg-violet-700 opacity-50 rounded-sm"></div>
                <span>Actual worked shift</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-semibold">Reviews:</span>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                  <span className="text-xs">5</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-lime-500 rounded-full"></div>
                  <span className="text-xs">4</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs">3</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span className="text-xs">2</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                  <span className="text-xs">1</span>
                </div>
              </div>
            </div>
          </div>

          {!hasShifts ? (
            <div className="p-8 text-center text-slate-500">
              No actual shifts found for this period
            </div>
          ) : (
            <>
              {/* Date Sections */}
              {sortedDates.map((date, dateIndex) => {
                const shifts = shiftsByDate[date];
                const dateSurveys = getSurveysForDate(date);

                // Sort shifts by start time
                const sortedShifts = [...shifts].sort((a, b) => {
                  const startA = a.shift_start || 0;
                  const startB = b.shift_start || 0;
                  return startA - startB;
                });

                return (
                  <div key={dateIndex} className="mb-6">
                    {/* Date Header - Outside Table */}
                    <div className="mb-3">
                      <h3 className="font-bold text-lg text-slate-900">
                        {formatDateReadableWithDayOfWeek(date)}
                      </h3>
                      {dateSurveys.length > 0 ? (
                        <p className="text-sm text-slate-600 mt-1">
                          {dateSurveys.length} guest survey{dateSurveys.length !== 1 ? 's' : ''} received
                        </p>
                      ) : (
                        <p className="text-sm text-red-600 mt-1 font-semibold">
                          No guest surveys received
                        </p>
                      )}
                    </div>

                    {/* Table for this date */}
                    <div className="border border-slate-900 overflow-hidden">
                      {/* Header Row */}
                      <div className="w-full border-b border-slate-900 flex">
                        <div className="w-[38%] flex py-2 border-r border-slate-900">
                          <div className="w-[40%] flex items-center px-4">
                            <p className="text-slate-900 text-xs font-bold">Employee</p>
                          </div>
                          <div className="w-[30%] flex items-center px-2">
                            <p className="text-slate-900 text-xs font-bold">Role</p>
                          </div>
                          <div className="w-[30%] text-center flex items-center justify-center">
                            <p className="text-slate-900 text-xs font-bold">Actual Shift</p>
                          </div>
                        </div>
                        <div className="w-[62%] flex">
                          {hasShifts && Array.from({ length: numOfHourBoxes }).map((_, index) => (
                            <div
                              key={index}
                              className={`${
                                index < numOfHourBoxes - 1 ? "border-r" : ""
                              } border-slate-900 flex flex-col`}
                              style={{ width: `${widthOfEachHourBox}%` }}
                            >
                              <div className="h-[70%] flex items-center justify-center">
                                <p className="text-slate-900 text-xs">
                                  {get_t_str_shortened_max(index + globalStartQhnum)}
                                </p>
                              </div>
                              <div className="h-[30%] flex">
                                <div className="w-[25%] border-r border-slate-900"></div>
                                <div className="w-[25%] border-r border-slate-900"></div>
                                <div className="w-[25%] border-r border-slate-900"></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Reviews Row */}
                      {dateSurveys.length > 0 ? (
                        <div className="w-full flex bg-slate-50 border-b border-slate-900">
                          {/* Left info panel */}
                          <div className="w-[38%] flex items-center border-r border-slate-900 px-4 py-3">
                            <p className="text-slate-700 text-sm font-semibold">Guest Surveys</p>
                          </div>

                        {/* Timeline panel with review markers */}
                        <div className="w-[62%] relative" style={{ minHeight: '50px' }}>
                          {dateSurveys.map((survey, surveyIndex) => {
                            const ratingColorClass = getRatingColor(survey.rating);
                            const surveyTimelineHour = getSurveyTimelineHour(survey);
                            const leftPosition = surveyTimelineHour * 4 * widthOfEachQuarterTick - globalStartQhnum * 4 * widthOfEachQuarterTick;
                            // Lower ratings should appear on top (higher z-index)
                            const containerZIndex = 20 + (6 - survey.rating) * 5;

                            return (
                              <div
                                key={surveyIndex}
                                className="absolute"
                                style={{
                                  left: `${leftPosition}%`,
                                  top: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  zIndex: containerZIndex,
                                }}
                              >
                                {/* Vertical line extending down through all shifts and chart */}
                                <div
                                  className={`absolute ${ratingColorClass}`}
                                  style={{
                                    top: '20px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '3px',
                                    height: `${sortedShifts.length * 50 + 100 + (laborChartData.summary.length > 0 ? 200 : 0)}px`,
                                    opacity: 0.5,
                                    zIndex: 1,
                                  }}
                                ></div>

                                {/* Colored rating circle */}
                                <Tooltip
                                  title={
                                    <div className="p-2">
                                      <div className="flex items-center gap-1 mb-1">
                                        {renderStars(survey.rating)}
                                        <span className="ml-1">({survey.rating})</span>
                                      </div>
                                      <div className="text-xs mb-1">
                                        <strong>Time:</strong> {getSurveyDisplayTime(survey)}
                                      </div>
                                      <div className="text-xs mb-1">
                                        <strong>Source:</strong> {survey.source} ({survey.timing_type})
                                      </div>
                                      {survey.feedback && (
                                        <div className="text-xs mb-1">
                                          <strong>Feedback:</strong> {survey.feedback.substring(0, 100)}
                                          {survey.feedback.length > 100 && '...'}
                                        </div>
                                      )}
                                      {survey.staff_members.length > 0 && (
                                        <div className="text-xs">
                                          <strong>Staff on duty:</strong> {survey.staff_members.map(s => formatName(s.name)).join(', ')}
                                        </div>
                                      )}
                                      <div className="text-xs mt-2 italic">Click for details</div>
                                    </div>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <div
                                    className={`cursor-pointer hover:scale-110 transition-transform ${ratingColorClass} rounded-full flex items-center justify-center shadow-lg border-2 border-white relative`}
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      zIndex: 10,
                                    }}
                                    onClick={() => handleOpenReviewModal(survey)}
                                  >
                                    <span className="text-white font-bold text-sm">
                                      {survey.rating}
                                    </span>
                                  </div>
                                </Tooltip>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}

                      {/* Employee Shift Rows */}
                      {sortedShifts.map((shift, shiftIndex) => {
                        const rawName = shift.store_team_member?.name ||
                                        shift.store_team_member?.first_name + ' ' + shift.store_team_member?.last_name ||
                                        'Unknown';
                        const employeeName = formatName(rawName);
                        const roleName = getRoleName(shift);

                        return (
                          <div key={shiftIndex} className={`w-full flex border-b border-slate-900 relative z-30`}>
                            {/* Left info panel */}
                            <div className="w-[38%] flex border-r border-slate-900">
                              <div className="w-[40%] flex items-center px-4 py-2">
                                <p className="text-slate-900 text-xs truncate">{employeeName}</p>
                              </div>
                              <div className="w-[30%] flex items-center px-2 py-2">
                                <p className="text-slate-600 text-xs truncate">{roleName}</p>
                              </div>
                              <div className="w-[30%] flex items-center justify-center py-2">
                                <p className="text-slate-900 text-xs">
                                  {shift.shift_start != null && shift.shift_end != null
                                    ? `${get_t_str_minute_precision(shift.shift_start)} - ${get_t_str_minute_precision(shift.shift_end)}`
                                    : '-'}
                                </p>
                              </div>
                            </div>

                            {/* Timeline panel */}
                            <div className="w-[62%] relative" style={{ minHeight: '40px' }}>
                              {/* Actual shift bar */}
                              {shift.shift_start != null && shift.shift_end != null && (
                                <div
                                  className="bg-violet-700 opacity-50 h-3 rounded-sm absolute top-[50%] transform -translate-y-1/2"
                                  style={{
                                    marginLeft: `${
                                      shift.shift_start * 4 * widthOfEachQuarterTick -
                                      globalStartQhnum * 4 * widthOfEachQuarterTick
                                    }%`,
                                    width: `${
                                      (shift.shift_end - shift.shift_start) * 4 * widthOfEachQuarterTick
                                    }%`,
                                  }}
                                ></div>
                              )}

                              {/* Break (if applicable) */}
                              {shift.break_start && shift.total_break > 0 && (
                                <div
                                  className="bg-violet-700 opacity-100 h-3 rounded-sm absolute top-[50%] transform -translate-y-1/2"
                                  style={{
                                    marginLeft: `${
                                      shift.break_start * 4 * widthOfEachQuarterTick -
                                      globalStartQhnum * 4 * widthOfEachQuarterTick
                                    }%`,
                                    width: `${shift.total_break * 4 * widthOfEachQuarterTick}%`,
                                  }}
                                ></div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Staffing Levels Chart Row */}
                      {laborChartData.summary.length > 0 && (
                        <div className="w-full flex bg-slate-50">
                          {/* Left label section to match table layout (38% width) */}
                          <div className="w-[38%] border-r border-slate-900 flex items-center justify-center py-4 relative z-30">
                            <div className="text-center">
                              <p className="text-sm font-bold text-slate-900">Staffing Levels</p>
                              <p className="text-xs text-slate-600 mt-1">Earned vs Used</p>
                            </div>
                          </div>

                          {/* Chart area aligned with timeline (62% width) */}
                          <div className="w-[62%]">
                            <VisualCustomLineChart
                              type="daily"
                              data={laborChartData.summary}
                              roleTypes={laborChartData.roleTypes}
                              supportNames={laborChartData.supportNames}
                              height={200}
                              isSmall={true}
                              label1={label1}
                              label2={label2}
                              start_h={minHour}
                              end_h={maxHour}
                              guestReviews={true}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Survey Summary */}
          {/* {surveys.length > 0 && (
            <div className="mt-6">
              <h3 className="font-bold text-lg mb-2">Survey Summary</h3>
              <div className="text-sm text-slate-600 mb-2">
                Total Surveys: {surveys.length} |
                Average Rating: {(surveys.reduce((sum, s) => sum + s.rating, 0) / surveys.length).toFixed(1)} ★
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[5, 4, 3, 2, 1].map(rating => {
                  const count = surveys.filter(s => s.rating === rating).length;
                  const percentage = ((count / surveys.length) * 100).toFixed(0);
                  return (
                    <div key={rating} className="text-center p-2 border rounded">
                      <div className="font-bold">{rating} ★</div>
                      <div className="text-sm">{count} ({percentage}%)</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )} */}
        </div>
      </Box>
      </Modal>

      {/* Detailed Review Modal */}
      <SurveyDetailModal
        open={openReviewModal}
        onClose={handleCloseReviewModal}
        survey={selectedReview}
        laborData={{
          summary: laborChartData.summary,
          label1: label1,
          label2: label2
        }}
      />
    </>
  );
}

export default GuestReviewOverlayModal;
